import React, { useState, useEffect } from 'react';
import { useClients } from '../contexts/ClientContext';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Slider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Instagram as InstagramIcon,
  Science as TestIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import {
  ClientSelector,
  DataTable,
  FormDialog,
  MetricCard,
  PageHeader,
  StatusChip,
} from '../components/primitives';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(13, 17, 23, 0.6)',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(48, 54, 61, 0.4)' },
    '&:hover fieldset': { borderColor: 'rgba(88, 166, 255, 0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#58a6ff' },
  },
  '& .MuiInputBase-input': { color: '#f0f6fc' },
  '& .MuiInputLabel-root': { color: '#7d8590' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#58a6ff' },
};

export default function InstagramAutomation() {
  const { clients } = useClients();
  const [selectedClient, setSelectedClient] = useState('');
  const [automationConfig, setAutomationConfig] = useState({
    follow_count_per_post: 5,
    follow_interval_days: 2,
    unfollow_count_per_day: 10,
    unfollow_interval_days: 2,
    unfollow_delay_days: 30,
    campaign_duration_days: 14,
  });
  const [targetProfiles, setTargetProfiles] = useState('');
  const [targetUnfollowUsernames, setTargetUnfollowUsernames] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [automationStats, setAutomationStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [testFollowUsername, setTestFollowUsername] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [listsData, setListsData] = useState(null);
  const [listsLoading, setListsLoading] = useState(false);
  const [listsTab, setListsTab] = useState(0);
  const [listsSearch, setListsSearch] = useState('');
  const [selectedToUnfollow, setSelectedToUnfollow] = useState([]);
  const [debugStats, setDebugStats] = useState(null);
  const [debugStatsLoading, setDebugStatsLoading] = useState(false);
  const [scheduledCampaign, setScheduledCampaign] = useState(null);
  const [scheduledCampaignLoading, setScheduledCampaignLoading] = useState(false);
  const [scheduledTestMode, setScheduledTestMode] = useState(true);

  useEffect(() => {
    fetchHistory();
    fetchDebugStats();
  }, []);

  useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      setSelectedClient(clients[0].id);
    }
  }, [clients, selectedClient]);

  useEffect(() => {
    if (credentials.username) fetchDebugStats();
  }, [credentials.username]);

  useEffect(() => {
    if (selectedClient) fetchAutomationStats();
  }, [selectedClient]);

  useEffect(() => {
    if (selectedClient) fetchAutomationConfig();
  }, [selectedClient, credentials.username]);

  useEffect(() => {
    if (credentials.username) fetchLists();
    else setListsData(null);
  }, [credentials.username]);

  useEffect(() => {
    if (credentials.username) fetchScheduledCampaign();
    else setScheduledCampaign(null);
  }, [credentials.username]);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    ...(localStorage.getItem('access_token') && {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    }),
  });

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await fetch(`/api/v1/instagram-automation/history?limit=100`, {
        headers: authHeaders(),
      });
      if (res.ok) setHistory((await res.json()) || []);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchAutomationStats = async () => {
    if (!selectedClient) return;
    try {
      const res = await fetch(`/api/v1/instagram-automation/stats/${selectedClient}`, {
        headers: authHeaders(),
      });
      if (res.ok) setAutomationStats(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAutomationConfig = async () => {
    if (!selectedClient) return;
    try {
      const params = new URLSearchParams({ client_id: String(selectedClient) });
      if (credentials.username) {
        params.set('instagram_username', credentials.username);
      }
      const res = await fetch(`/api/v1/instagram-automation/configure?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      const cfg = data.config || {};
      setAutomationConfig((prev) => ({
        ...prev,
        follow_count_per_post: cfg.follow_count_per_post ?? prev.follow_count_per_post,
        follow_interval_days: cfg.follow_interval_days ?? prev.follow_interval_days,
        unfollow_count_per_day: cfg.unfollow_count_per_day ?? prev.unfollow_count_per_day,
        unfollow_interval_days: cfg.unfollow_interval_days ?? prev.unfollow_interval_days,
        unfollow_delay_days: cfg.unfollow_delay_days ?? prev.unfollow_delay_days,
        campaign_duration_days: cfg.campaign_duration_days ?? prev.campaign_duration_days,
      }));
      if (!credentials.username && cfg.instagram_username) {
        setCredentials((p) => ({ ...p, username: cfg.instagram_username }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchScheduledCampaign = async () => {
    if (!credentials.username) return;
    try {
      setScheduledCampaignLoading(true);
      const res = await fetch(`/api/v1/instagram-automation/scheduled-campaign?instagram_username=${encodeURIComponent(credentials.username)}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.active) setScheduledCampaign(data.schedule);
      else setScheduledCampaign(null);
    } catch {
      setScheduledCampaign(null);
    } finally {
      setScheduledCampaignLoading(false);
    }
  };

  const fetchDebugStats = async () => {
    try {
      setDebugStatsLoading(true);
      const params = new URLSearchParams({ days: 7 });
      if (credentials.username) params.set('instagram_username', credentials.username);
      const res = await fetch(`/api/v1/instagram-automation/debug-stats?${params}`, {
        headers: authHeaders(),
      });
      if (res.ok) setDebugStats(await res.json());
      else setDebugStats(null);
    } catch (err) {
      console.error(err);
      setDebugStats(null);
    } finally {
      setDebugStatsLoading(false);
    }
  };

  const fetchLists = async () => {
    if (!credentials.username) return;
    try {
      setListsLoading(true);
      const res = await fetch(`/api/v1/instagram-automation/lists?instagram_username=${encodeURIComponent(credentials.username)}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok) setListsData(data);
      else setListsData(null);
    } catch (err) {
      console.error(err);
      setListsData(null);
    } finally {
      setListsLoading(false);
    }
  };

  const handleScrapeLists = async () => {
    if (!credentials.username || !credentials.password) {
      setError('Enter credentials');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/v1/instagram-automation/scrape-lists', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          client_id: selectedClient ? parseInt(selectedClient) : null,
          instagram_username: credentials.username,
          instagram_password: credentials.password,
          limit: 1500,
        }),
      });
      const text = await res.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        setError('Server returned an error page (possibly timeout). The scrape may have completed—try Refresh to load results.');
        fetchLists();
        return;
      }
      if (res.ok) {
        setSuccess(data.message || 'Lists scraped.');
        fetchLists();
        fetchDebugStats();
      } else setError(data.detail || 'Scrape failed');
    } catch (e) {
      setError(e.message || 'Scrape failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollowFromList = async () => {
    if (!selectedToUnfollow.length || !credentials.username || !credentials.password) {
      setError('Select usernames to unfollow');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/v1/instagram-automation/unfollow-from-list', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          client_id: selectedClient ? parseInt(selectedClient) : null,
          instagram_username: credentials.username,
          instagram_password: credentials.password,
          usernames: selectedToUnfollow,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || 'Unfollow complete.');
        setSelectedToUnfollow([]);
        fetchLists();
        fetchHistory();
        fetchDebugStats();
      } else setError(data.detail || 'Unfollow failed');
    } catch (e) {
      setError(e.message || 'Unfollow failed');
    } finally {
      setLoading(false);
    }
  };

  const apiCall = async (url, body) => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || 'Done.');
        fetchHistory();
        fetchDebugStats();
        if (selectedClient) fetchAutomationStats();
        return { ok: true, data };
      }
      const err = Array.isArray(data.detail)
        ? data.detail.map((d) => d.msg || d).join(', ')
        : data.detail || data.error;
      setError(err || 'Request failed');
      return { ok: false };
    } catch (e) {
      setError(e.message || 'Request failed');
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  const handleTestFollow = () => {
    if (!credentials.username || !credentials.password) {
      setError('Enter credentials');
      return;
    }
    apiCall('/api/v1/instagram-automation/test-follow', {
      client_id: selectedClient ? parseInt(selectedClient) : null,
      instagram_username: credentials.username,
      instagram_password: credentials.password,
      target_username: testFollowUsername.trim().replace(/^@/, '') || null,
    });
  };

  const handleEnsureSession = () => {
    if (!selectedClient) {
      setError('Select a client first — each client needs its own Instagram browser session');
      return;
    }
    if (!credentials.username || !credentials.password) {
      setError('Enter credentials');
      return;
    }
    apiCall('/api/v1/instagram-automation/ensure-session', {
      client_id: parseInt(selectedClient, 10),
      instagram_username: credentials.username,
      instagram_password: credentials.password,
    });
  };

  const handleFollowCampaign = () => {
    const profiles = targetProfiles.split(',').map((p) => p.trim()).filter(Boolean);
    if (!credentials.username || !credentials.password) {
      setError('Enter credentials');
      return;
    }
    apiCall('/api/v1/instagram-automation/follow-campaign', {
      client_id: selectedClient ? parseInt(selectedClient) : null,
      target_profiles: profiles.length ? profiles : null,
      instagram_username: credentials.username,
      instagram_password: credentials.password,
      follow_count_per_post: automationConfig.follow_count_per_post,
    });
  };

  const handleStartScheduledCampaign = async () => {
    if (!credentials.username || !credentials.password) {
      setError('Enter credentials');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const profiles = targetProfiles.split(',').map((p) => p.trim()).filter(Boolean);
      const res = await fetch('/api/v1/instagram-automation/start-scheduled-campaign', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          client_id: selectedClient ? parseInt(selectedClient) : null,
          instagram_username: credentials.username,
          instagram_password: credentials.password,
          target_profiles: profiles.length ? profiles : null,
          follow_count_per_source: automationConfig.follow_count_per_post,
          unfollow_count_per_run: automationConfig.unfollow_count_per_day,
          follow_interval_days: automationConfig.follow_interval_days,
          unfollow_interval_days: automationConfig.unfollow_interval_days,
          unfollow_delay_days: automationConfig.unfollow_delay_days,
          campaign_duration_days: automationConfig.campaign_duration_days,
          test_mode: scheduledTestMode,
          working_hours_start: automationConfig.working_hours_start,
          working_hours_end: automationConfig.working_hours_end,
          timezone: automationConfig.timezone,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || 'Scheduled campaign started.');
        fetchScheduledCampaign();
      } else setError(data.detail || 'Failed to start scheduled campaign');
    } catch (e) {
      setError(e.message || 'Failed to start');
    } finally {
      setLoading(false);
    }
  };

  const handleStopScheduledCampaign = async () => {
    if (!credentials.username) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/instagram-automation/stop-scheduled-campaign?instagram_username=${encodeURIComponent(credentials.username)}`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || 'Campaign stopped.');
        setScheduledCampaign(null);
      } else setError(data.detail || 'Failed to stop');
    } catch (e) {
      setError(e.message || 'Failed to stop');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfollowCampaign = () => {
    const usernames = targetUnfollowUsernames
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (!credentials.username || !credentials.password) {
      setError('Enter credentials');
      return;
    }
    apiCall('/api/v1/instagram-automation/unfollow-campaign', {
      client_id: selectedClient ? parseInt(selectedClient) : null,
      target_usernames: usernames.length ? usernames : null,
      instagram_username: credentials.username,
      instagram_password: credentials.password,
    });
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      setError('');
      if (!selectedClient) {
        setError('Select a client before saving automation settings');
        return;
      }
      if (!credentials.username) {
        setError('Enter Instagram username before saving automation settings');
        return;
      }
      const res = await fetch('/api/v1/instagram-automation/configure', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          client_id: parseInt(selectedClient, 10),
          instagram_username: credentials.username,
          ...automationConfig,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const cfg = data.config || {};
        setAutomationConfig((prev) => ({
          ...prev,
          follow_count_per_post: cfg.follow_count_per_post ?? prev.follow_count_per_post,
          follow_interval_days: cfg.follow_interval_days ?? prev.follow_interval_days,
          unfollow_count_per_day: cfg.unfollow_count_per_day ?? prev.unfollow_count_per_day,
          unfollow_interval_days: cfg.unfollow_interval_days ?? prev.unfollow_interval_days,
          unfollow_delay_days: cfg.unfollow_delay_days ?? prev.unfollow_delay_days,
          campaign_duration_days: cfg.campaign_duration_days ?? prev.campaign_duration_days,
        }));
        setSuccess(data.message || 'Automation settings saved');
        setConfigDialogOpen(false);
      } else setError(data.detail || 'Save failed');
    } catch (e) {
      setError('Save failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedClientData = clients.find((c) => c.id === selectedClient);

  return (
    <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
      <PageHeader
        title="Instagram Follow / Unfollow"
        subtitle={
          <>
            Automate follow campaigns (mine from competitor followers / brand hashtags) and unfollow people who don&apos;t follow back. Set{' '}
            <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: 4 }}>PUPPETEER_HEADLESS=false</code>{' '}
            in .env to watch the browser.
          </>
        }
        icon={<InstagramIcon sx={{ fontSize: 28, color: '#e4405f' }} />}
      />

      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>Mac worker required.</strong> Follow/Unfollow uses Puppeteer (not Graph API) and needs a residential
        browser session. Run Puppeteer on your Mac and tunnel Redis/Postgres (or reverse-tunnel port 3002 to the VPS).
        See the operator runbook: <code>docs/INSTAGRAM_FOLLOW_UNFOLLOW_RUNBOOK.md</code>. Each client has its own
        Chromium profile (per-client <code>userDataDir</code>) — run <strong>Ensure session</strong> once per
        client. Leave target profiles blank to auto-discover from this client&apos;s competitor Instagram URLs and
        brand hashtags. Daily follow/unfollow caps are enforced from automation logs (UTC day).
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(248,81,73,0.1)', color: '#f85149' }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, bgcolor: 'rgba(56,139,253,0.1)', color: '#58a6ff' }}>
          {success}
        </Alert>
      )}

      {/* 1. Credentials */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            1. Instagram credentials
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Username"
                value={credentials.username}
                onChange={(e) => setCredentials((p) => ({ ...p, username: e.target.value }))}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label="Password"
                value={credentials.password}
                onChange={(e) => setCredentials((p) => ({ ...p, password: e.target.value }))}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <ClientSelector
                clients={clients}
                value={selectedClient || ''}
                onChange={(client) => setSelectedClient(client?.id || '')}
                label="Client (optional)"
                allowEmpty
                emptyLabel="None"
                fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 2. Quick tests */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(48,54,61,0.5)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TestIcon fontSize="small" />
            2. Quick tests (watch the browser)
          </Typography>
          <Typography variant="body2" sx={{ color: '#7d8590', mb: 2 }}>
            Follow: client page → followers → pick one → their followers → follow 1. For unfollow, use the list below to select names and click Unfollow selected.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Source profile (optional — blank = your IG)"
              value={testFollowUsername}
              onChange={(e) => setTestFollowUsername(e.target.value)}
              sx={{ width: 240, ...inputSx }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleEnsureSession}
              disabled={loading}
              sx={{ borderColor: '#58a6ff', color: '#58a6ff' }}
            >
              Ensure Session (login once)
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleTestFollow}
              disabled={loading}
              sx={{ bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
            >
              Test Follow 1
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* 3. Campaigns */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(48,54,61,0.5)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StartIcon fontSize="small" />
            3. Campaigns
          </Typography>
          <Typography variant="body2" sx={{ color: '#7d8590', mb: 2 }}>
            Follow: blank = auto-discover competitor IG handles + brand hashtags (else your IG). Unfollow: blank = stored Not following back list. Use Configure to set follow/unfollow intervals and campaign duration.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2, bgcolor: 'rgba(210,153,34,0.12)', color: '#d29922' }}>
            Unattended follow/unfollow can trigger Instagram checkpoints or temporary action blocks.
            Start with <strong>test mode</strong> on one client, conservative daily caps, and daytime windows.
            See <code>docs/INSTAGRAM_FOLLOW_UNFOLLOW_RUNBOOK.md</code> (Phase 18.3).
          </Alert>

          {scheduledCampaign && (
            <Alert
              severity={
                scheduledCampaign.last_run_status &&
                (scheduledCampaign.last_run_status.includes('failed') ||
                  scheduledCampaign.last_run_status === 'deferred')
                  ? 'warning'
                  : 'info'
              }
              sx={{ mb: 2, bgcolor: 'rgba(56,139,253,0.1)', color: '#58a6ff' }}
            >
              Scheduled campaign {scheduledCampaign.test_mode ? '(test mode)' : '(production)'}: follow every{' '}
              {scheduledCampaign.follow_interval_days} days, unfollow every {scheduledCampaign.unfollow_interval_days} days.
              Window {scheduledCampaign.working_hours_start}:00–{scheduledCampaign.working_hours_end}:00{' '}
              {scheduledCampaign.timezone || 'America/New_York'}.
              Last follow: {scheduledCampaign.last_follow_run ? new Date(scheduledCampaign.last_follow_run).toLocaleString() : 'not yet'}
              {scheduledCampaign.last_follow_count != null ? ` (${scheduledCampaign.last_follow_count})` : ''}.
              Last unfollow: {scheduledCampaign.last_unfollow_run ? new Date(scheduledCampaign.last_unfollow_run).toLocaleString() : 'not yet'}
              {scheduledCampaign.last_unfollow_count != null ? ` (${scheduledCampaign.last_unfollow_count})` : ''}.
              {scheduledCampaign.last_run_status && (
                <> Beat status: <strong>{scheduledCampaign.last_run_status}</strong>
                  {scheduledCampaign.last_run_detail ? ` — ${scheduledCampaign.last_run_detail}` : ''}.
                </>
              )}
              <Button size="small" onClick={handleStopScheduledCampaign} sx={{ ml: 1, color: '#f85149' }}>Stop</Button>
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="Source profiles (optional — blank = your IG)"
            value={targetProfiles}
            onChange={(e) => setTargetProfiles(e.target.value)}
            placeholder="blank = discover competitors/hashtags, or competitor1, competitor2"
            sx={{ mb: 2, ...inputSx }}
          />
          <TextField
            fullWidth
            size="small"
            label="Usernames to unfollow (optional — blank = use Not following back list)"
            value={targetUnfollowUsernames}
            onChange={(e) => setTargetUnfollowUsernames(e.target.value)}
            placeholder="blank = from stored list, or user1, user2"
            sx={{ mb: 2, ...inputSx }}
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<StartIcon />}
              onClick={handleFollowCampaign}
              disabled={loading}
              sx={{ bgcolor: '#58a6ff', '&:hover': { bgcolor: '#1f6feb' } }}
            >
              Run Follow Campaign
            </Button>
            <Button
              variant="contained"
              startIcon={<StartIcon />}
              onClick={handleUnfollowCampaign}
              disabled={loading}
              sx={{ bgcolor: '#f85149', '&:hover': { bgcolor: '#da3633' } }}
            >
              Run Unfollow Campaign
            </Button>
            <Button
              variant="contained"
              startIcon={<StartIcon />}
              onClick={handleStartScheduledCampaign}
              disabled={loading || !!scheduledCampaign}
              sx={{ bgcolor: '#238636', '&:hover': { bgcolor: '#2ea043' } }}
            >
              Start Scheduled Campaign
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={scheduledTestMode}
                  onChange={(e) => setScheduledTestMode(e.target.checked)}
                  disabled={!!scheduledCampaign}
                  sx={{ color: '#58a6ff', '&.Mui-checked': { color: '#58a6ff' } }}
                />
              }
              label="Test mode (runs without INSTAGRAM_SCHEDULED_CAMPAIGNS_ENABLED)"
              sx={{ color: '#c9d1d9', alignSelf: 'center' }}
            />
            <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setConfigDialogOpen(true)} sx={{ color: '#58a6ff', borderColor: '#58a6ff' }}>
              Configure
            </Button>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: '#7d8590' }}>
                Running…
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 3a. Success rates (debug) */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(48,54,61,0.5)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon fontSize="small" />
              Success rates (last 7 days)
            </Typography>
            <Button size="small" onClick={fetchDebugStats} disabled={debugStatsLoading} sx={{ color: '#58a6ff' }}>
              {debugStatsLoading ? 'Loading…' : 'Refresh'}
            </Button>
          </Box>
          {debugStats ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Follow: ${debugStats.follow?.rate || 'N/A'} (${debugStats.follow?.succeeded || 0}/${debugStats.follow?.attempted || 0})`}
                  size="small"
                  sx={{ bgcolor: debugStats.follow?.attempted > 0 ? 'rgba(40,167,69,0.2)' : 'rgba(48,54,61,0.5)', color: '#58a6ff' }}
                />
                <Chip
                  label={`Unfollow: ${debugStats.unfollow?.rate || 'N/A'} (${debugStats.unfollow?.succeeded || 0}/${debugStats.unfollow?.attempted || 0})`}
                  size="small"
                  sx={{ bgcolor: debugStats.unfollow?.attempted > 0 ? 'rgba(40,167,69,0.2)' : 'rgba(48,54,61,0.5)', color: '#58a6ff' }}
                />
              </Box>
              {debugStats.recent_failures?.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#7d8590', display: 'block', mb: 1 }}>Recent failures</Typography>
                  <Box sx={{ maxHeight: 120, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, p: 1 }}>
                    {debugStats.recent_failures.map((f, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: '#f85149', fontFamily: 'monospace' }}>
                        {f.action} @{f.target}: {f.error}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : debugStatsLoading ? (
            <Typography variant="body2" sx={{ color: '#7d8590' }}>Loading…</Typography>
          ) : (
            <Typography variant="body2" sx={{ color: '#7d8590' }}>Run campaigns to see success rates.</Typography>
          )}
        </CardContent>
      </Card>

      {/* 3b. Followers & following lists (when credentials entered) */}
      {credentials.username && (
        <Card sx={{ mb: 3, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(48,54,61,0.5)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#f0f6fc', display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon fontSize="small" />
                Followers & following
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={listsLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  onClick={handleScrapeLists}
                  disabled={loading || listsLoading || !credentials.password}
                  sx={{ bgcolor: '#58a6ff', '&:hover': { bgcolor: '#1f6feb' } }}
                >
                  {listsLoading ? 'Scraping…' : 'Load followers & following'}
                </Button>
                <Button size="small" onClick={fetchLists} disabled={listsLoading} sx={{ color: '#58a6ff' }}>
                  Refresh
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: '#7d8590', mb: 2 }}>
              Scrape once, then review. Compare followers vs following to find who isn&apos;t following back. Unfollow by selecting names.
            </Typography>
            {listsData?.success ? (
              <>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`${listsData.followers?.length || 0} followers`} size="small" sx={{ bgcolor: 'rgba(88,166,255,0.2)', color: '#58a6ff' }} />
                  <Chip label={`${listsData.following?.length || 0} following`} size="small" sx={{ bgcolor: 'rgba(88,166,255,0.2)', color: '#58a6ff' }} />
                  <Chip label={`${listsData.not_following_back?.length || 0} not following back`} size="small" sx={{ bgcolor: 'rgba(248,81,73,0.2)', color: '#f85149' }} />
                  {listsData.scraped_at && (
                    <Typography variant="caption" sx={{ color: '#7d8590', alignSelf: 'center' }}>
                      Scraped {new Date(listsData.scraped_at).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <TextField
                  size="small"
                  placeholder="Search names…"
                  value={listsSearch}
                  onChange={(e) => setListsSearch(e.target.value)}
                  sx={{ mb: 2, width: 280, ...inputSx }}
                />
                <Tabs value={listsTab} onChange={(_, v) => setListsTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tab label="Followers" sx={{ color: '#7d8590' }} />
                  <Tab label="Following" sx={{ color: '#7d8590' }} />
                  <Tab label="Not following back" sx={{ color: '#f85149' }} />
                </Tabs>
                {listsTab === 0 && (
                  <List dense sx={{ maxHeight: 280, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                    {(listsData.followers || [])
                      .filter((u) => !listsSearch || u.toLowerCase().includes(listsSearch.toLowerCase()))
                      .map((u) => (
                        <ListItem key={u} disablePadding>
                          <ListItemText primary={`@${u}`} sx={{ color: '#c9d1d9', '& .MuiListItemText-primary': { fontSize: '0.9rem' } }} />
                        </ListItem>
                      ))}
                    {!(listsData.followers || []).filter((u) => !listsSearch || u.toLowerCase().includes(listsSearch.toLowerCase())).length && (
                      <ListItem><ListItemText primary="No matches" sx={{ color: '#7d8590' }} /></ListItem>
                    )}
                  </List>
                )}
                {listsTab === 1 && (
                  <List dense sx={{ maxHeight: 280, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                    {(listsData.following || [])
                      .filter((u) => !listsSearch || u.toLowerCase().includes(listsSearch.toLowerCase()))
                      .map((u) => (
                        <ListItem key={u} disablePadding>
                          <ListItemText primary={`@${u}`} sx={{ color: '#c9d1d9', '& .MuiListItemText-primary': { fontSize: '0.9rem' } }} />
                        </ListItem>
                      ))}
                    {!(listsData.following || []).filter((u) => !listsSearch || u.toLowerCase().includes(listsSearch.toLowerCase())).length && (
                      <ListItem><ListItemText primary="No matches" sx={{ color: '#7d8590' }} /></ListItem>
                    )}
                  </List>
                )}
                {listsTab === 2 && (
                  <>
                    <List dense sx={{ maxHeight: 280, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                      {(listsData.not_following_back || [])
                        .filter((u) => !listsSearch || u.toLowerCase().includes(listsSearch.toLowerCase()))
                        .map((u) => (
                          <ListItem key={u} disablePadding>
                            <ListItemButton
                              onClick={() => setSelectedToUnfollow((prev) => (prev.includes(u) ? prev.filter((x) => x !== u) : [...prev, u]))}
                            >
                              <Checkbox checked={selectedToUnfollow.includes(u)} size="small" sx={{ color: '#58a6ff', '&.Mui-checked': { color: '#58a6ff' } }} />
                              <ListItemText primary={`@${u}`} sx={{ color: '#c9d1d9', '& .MuiListItemText-primary': { fontSize: '0.9rem' } }} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      {!(listsData.not_following_back || []).filter((u) => !listsSearch || u.toLowerCase().includes(listsSearch.toLowerCase())).length && (
                        <ListItem><ListItemText primary="No matches" sx={{ color: '#7d8590' }} /></ListItem>
                      )}
                    </List>
                    {selectedToUnfollow.length > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleUnfollowFromList}
                        disabled={loading}
                        sx={{ mt: 2, bgcolor: '#f85149', '&:hover': { bgcolor: '#da3633' } }}
                      >
                        Unfollow {selectedToUnfollow.length} selected
                      </Button>
                    )}
                  </>
                )}
              </>
            ) : listsData?.success === false ? (
              <Typography variant="body2" sx={{ color: '#7d8590' }}>{listsData.message || 'No lists. Click Load followers & following to scrape.'}</Typography>
            ) : listsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /><Typography variant="body2" sx={{ color: '#7d8590' }}>Loading…</Typography></Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#7d8590' }}>Enter credentials and click Load followers & following to scrape. Lists are saved to files for review.</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* 4. History */}
      <Card sx={{ mb: 3, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(48,54,61,0.5)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon fontSize="small" />
              4. History
            </Typography>
            <Button size="small" onClick={fetchHistory} disabled={historyLoading} sx={{ color: '#58a6ff' }}>
              {historyLoading ? 'Loading…' : 'Refresh'}
            </Button>
          </Box>
          <DataTable
            loading={historyLoading}
            empty={!historyLoading && history.length === 0}
            emptyMessage="No actions yet. Try a quick test or run a campaign."
            stickyHeader
            sx={{ maxHeight: 350 }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ fontSize: '0.85rem' }}>
                    {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.action}
                      size="small"
                      sx={{
                        bgcolor: row.action === 'follow' ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)',
                        color: row.action === 'follow' ? '#28a745' : '#dc3545',
                      }}
                    />
                  </TableCell>
                  <TableCell>@{row.target_username}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{row.instagram_username}</TableCell>
                  <TableCell>
                    {row.success ? (
                      <StatusChip label="OK" color="success" variant="filled" />
                    ) : (
                      <StatusChip status="failed" label={row.error_message || 'Failed'} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </CardContent>
      </Card>

      {/* Stats (when client selected) */}
      {automationStats && selectedClientData && (
        <Card sx={{ mb: 3, bgcolor: 'rgba(22,27,34,0.9)', border: '1px solid rgba(48,54,61,0.5)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon fontSize="small" />
              Stats — {selectedClientData.name || selectedClientData.company}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Followed"
                  value={automationStats.total_followed}
                  accentColor="#58a6ff"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Unfollowed"
                  value={automationStats.total_unfollowed}
                  accentColor="#da3633"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Following"
                  value={automationStats.current_following}
                  accentColor="#58a6ff"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <MetricCard
                  title="Followers"
                  value={automationStats.current_followers}
                  accentColor="#a371f7"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Configure dialog */}
      <FormDialog
        open={configDialogOpen}
        maxWidth="sm"
        title="Campaign settings"
        submitLabel="Save"
        loading={loading}
        onSubmit={handleSaveConfig}
        onClose={() => setConfigDialogOpen(false)}
      >
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Follows per source"
              type="number"
              value={automationConfig.follow_count_per_post}
              onChange={(e) => setAutomationConfig((p) => ({ ...p, follow_count_per_post: parseInt(e.target.value) || 5 }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Unfollows per run"
              type="number"
              value={automationConfig.unfollow_count_per_day}
              onChange={(e) => setAutomationConfig((p) => ({ ...p, unfollow_count_per_day: parseInt(e.target.value) || 10 }))}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Follow interval (days)"
              type="number"
              value={automationConfig.follow_interval_days}
              onChange={(e) => setAutomationConfig((p) => ({ ...p, follow_interval_days: Math.max(1, parseInt(e.target.value) || 2) }))}
              helperText="Run follow campaign every N days"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Unfollow interval (days)"
              type="number"
              value={automationConfig.unfollow_interval_days}
              onChange={(e) => setAutomationConfig((p) => ({ ...p, unfollow_interval_days: Math.max(1, parseInt(e.target.value) || 2) }))}
              helperText="Run unfollow campaign every N days"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Unfollow delay (days)"
              type="number"
              value={automationConfig.unfollow_delay_days}
              onChange={(e) => setAutomationConfig((p) => ({ ...p, unfollow_delay_days: parseInt(e.target.value) || 30 }))}
              helperText="Only unfollow after following 30+ days"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Campaign duration (days)"
              type="number"
              value={automationConfig.campaign_duration_days}
              onChange={(e) => setAutomationConfig((p) => ({ ...p, campaign_duration_days: Math.max(1, parseInt(e.target.value) || 14) }))}
              helperText="How long the scheduled campaign runs"
            />
          </Grid>
        </Grid>
      </FormDialog>
    </Box>
  );
}
